import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class TaxAttributesService {

  constructor(private http: HttpClient) { }

  // Generic method to load JSON object data
  loadJsonObject(
    baseUrl: string,
    requestParams: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build payload EXACTLY like original AngularJS with proper fallbacks
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);

    // Add request specific parameters (these should take priority)
    Object.keys(requestParams).forEach(key => {
      params[key] = requestParams[key];
    });

    // 🔴 Only add fallbacks for parameters NOT explicitly provided in requestParams
    if (!requestParams.hasOwnProperty('tax_year')) {
      params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    }
    if (!requestParams.hasOwnProperty('scenario')) {
      params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    }
    if (!requestParams.hasOwnProperty('jcd_key')) {
      params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    }
    if (!requestParams.hasOwnProperty('scenarioCode')) {
      params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    }

    // 🔴 Use query string parameters like AngularJS JsonObjectFactory.getJSONObj
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    // Use GET request with query parameters (like AngularJS)
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Save tax attributes data
  saveTaxAttributes(
    baseUrl: string,
    clobSettings: any,
    taxAttributesDetails: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=dadsmj`;

    // Build payload EXACTLY like original AngularJS with proper fallbacks
    let params = _.merge({}, globalService || {}, filterParams);

    // Essential parameters with fallbacks
    params.clob_settings = JSON.stringify(clobSettings);  // Note: plural "settings"
    params.clob_data = JSON.stringify(taxAttributesDetails);
    params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";
    params.process_name = "Tax-Attributes-grid-save";

    // checkLockedbj — legacy builder (GlobalServices.js:1934-1944), full 7-field shape with fallbacks
    const checkLockedbj = [{
      leid: rowData.LEID || rowData.SOURCE_LEID,
      me_code: rowData.ME_CODE || rowData.ME || rowData.SOURCE_ME_CODE || rowData.MARS,
      cdr_number: rowData.CDR_NO || rowData.TAX_CODE || rowData.SOURCE_CDR_NO,
      consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.FILING_GROUP,
      reporting_period: rowData.REPORTING_PERIOD || rowData.SOURCE_REPORTING_PERIOD,
      combination_key: rowData.COMBINATION_KEY || rowData.COMPL_COMBINATION_KEY || rowData.HO_COMBINATION_KEY,
      scenario_key: rowData.SCENARIO_KEY || filterParams.scenario || globalService?.scenario
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  // Save tax attributes entity details
  saveTaxAttributesEntityDtls(
    baseUrl: string,
    clobSettings: any,
    entityDetails: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=8xzfoo`;

    let params = _.merge({}, globalService || {}, filterParams);

    params.clob_settings = JSON.stringify(clobSettings);
    params.clob_data = JSON.stringify(entityDetails);
    params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";
    params.process_name = "Tax-Attributes-entity-save";

    // checkLockedbj — legacy parity: the original entity-save payload carries
    // exactly four fields for the entity being saved (leid / me_code = posting ME /
    // cdr_number / reporting_period). Built from clob_data so structure and values
    // match the original. Extra keys (combination_key, scenario_key, ...) are NOT
    // sent — the backend lock check for this action expects only these four.
    const lockSrc = Array.isArray(entityDetails) ? entityDetails[0] : entityDetails;
    const checkLockedbj = [{
      leid: lockSrc?.leid ?? clobSettings.leid,
      me_code: lockSrc?.posting_meCode,
      cdr_number: lockSrc?.cdr_No ?? clobSettings.cdr_No,
      reporting_period: lockSrc?.reporting_period ?? clobSettings.reporting_period
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  // Delete utilized details
  deleteUtilDtls(
    baseUrl: string,
    clobSettings: any,
    deleteDetails: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=02dmm7`;

    let params = _.merge({}, globalService || {}, filterParams);

    params.clob_settings = JSON.stringify(clobSettings);
    params.clob_data = JSON.stringify(deleteDetails);
    params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";
    params.process_name = "Tax-Attributes-util-delete";

    const checkLockedbj = [{
      leid: rowData.LEID || rowData.SOURCE_LEID,
      me_code: rowData.ME_CODE || rowData.ME || rowData.SOURCE_ME_CODE || rowData.MARS,
      cdr_number: rowData.CDR_NO || rowData.TAX_CODE || rowData.SOURCE_CDR_NO,
      consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.FILING_GROUP,
      reporting_period: rowData.REPORTING_PERIOD || rowData.SOURCE_REPORTING_PERIOD,
      combination_key: rowData.COMBINATION_KEY || rowData.COMPL_COMBINATION_KEY || rowData.HO_COMBINATION_KEY,
      scenario_key: rowData.SCENARIO_KEY || filterParams.scenario || globalService?.scenario || parseInt(filterParams.scenario) || 1
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  // Save posting entity
  savePostingEntity(
    baseUrl: string,
    clobSettings: any,
    postingDetails: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=gh55zt`;

    let params = _.merge({}, globalService || {}, filterParams);

    params.clob_settings = JSON.stringify(clobSettings);
    params.clob_data = JSON.stringify(postingDetails);
    params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";
    params.process_name = "Tax-Attributes-posting-entity-save";
    params.sso_id = clobSettings.sso_id;

    // Lock-check object for the source entity being re-pointed. Legacy parity:
    // the original posting-entity payload carries checkLockedbj built from the
    // source entity (leid / me_code = posting ME / cdr_number / reporting_period).
    // Sourced from clob_data so the structure and values match the legacy app.
    const checkLockedbj = [{
      leid: postingDetails.leid,
      me_code: postingDetails.posting_meCode,
      cdr_number: postingDetails.cdr_No,
      reporting_period: postingDetails.reporting_period
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
